"use client";

import type { FC } from "react";
import { motion } from "motion/react";
import { OnboardingBubbleTail } from "../quiz/OnboardingBubbleTail";
import { OnboardingEdAvatar } from "../quiz/OnboardingEdAvatar";
import { ResultAnimatedHeroValue } from "../quiz/ResultAnimatedHeroValue";
import { ResultChartCard } from "../result";
import { ResultIdleCashHeroValue } from "../result/ResultIdleCashHeroValue";
import { ResultRunwayHeroValue } from "../result/ResultRunwayHeroValue";
import type { ResultUIData } from "@/src/hooks/onboarding/mapResultValuesToResultUI";
import { RESULT_VARIANT_CONFIG } from "../quizStepsConfig";
import { OnboardingEmphasisCtaButton } from "../shared/OnboardingEmphasisCtaButton";

interface ResultStepProps {
  resultUI: ResultUIData;
  onContinue: () => void;
  loading?: boolean;
}

export const ResultStep: FC<ResultStepProps> = ({
  resultUI,
  onContinue,
  loading = false,
}) => (
  <div
    className="fixed inset-0 z-10 flex flex-col overflow-hidden bg-background_solid_neutral_secondary pt-safe md:static md:z-auto md:min-h-dvh md:pt-0"
    data-testid="onboarding-result-step"
    data-result-variant={resultUI.variant}
    data-result-has-data={resultUI.hasData ? "true" : "false"}
    data-figma-node={RESULT_VARIANT_CONFIG[resultUI.variant].figmaNodeId}
    data-layout="result-responsive"
    data-layout-h5="result-viewport-shell"
  >
    <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col md:max-w-[480px] md:px-4">
      <div
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain p-6 md:px-0 md:pt-16"
        data-testid="result-scroll-view"
      >
        <div
          className="flex items-start gap-2.5"
          data-testid="result-ed-bubble"
        >
          <OnboardingEdAvatar />
          <div className="relative flex flex-1 flex-col items-start gap-1 rounded-lg bg-background_brand_overlay10 px-4 py-3.5">
            <OnboardingBubbleTail variant="light" />
            <div
              className="flex w-full flex-col items-start gap-0.5"
              data-testid="result-ed-bubble-copy"
            >
              <p className="body_medium_s m-0 text-content_brand_primary">
                {resultUI.bubbleLabel}
              </p>
              <p className="title_l m-0 text-content_primary">
                {resultUI.bubbleQuestion}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2" data-testid="result-hero-block">
            <p className="title_xl text-content_primary">{resultUI.heroLine}</p>
            {resultUI.variant === "runway" ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ResultRunwayHeroValue value={resultUI.heroValue} />
              </motion.div>
            ) : resultUI.variant === "idle-cash" ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ResultIdleCashHeroValue value={resultUI.heroValue} />
              </motion.div>
            ) : (
              <motion.p
                className="display_s text-content_primary"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ResultAnimatedHeroValue value={resultUI.heroValue} />
              </motion.p>
            )}
            {resultUI.heroSubline ? (
              <p className="body_medium_m text-content_tertiary">
                {resultUI.heroSubline}
              </p>
            ) : null}
          </div>

          <ResultChartCard
            chart={resultUI.chart}
            payoffTitle={resultUI.payoffTitle}
            payoffBody={resultUI.payoffBody}
            hasData={resultUI.hasData}
          />
        </div>
      </div>

      <footer
        className="shrink-0 bg-background_solid_neutral_secondary px-4 pt-2 pb-[calc(16px+env(safe-area-inset-bottom,0px))] md:px-0 md:pb-10"
        data-testid="result-floating-cta-footer"
        data-layout="result-floating-cta"
      >
        <OnboardingEmphasisCtaButton
          testId="result-continue-button"
          label={resultUI.cta}
          loading={loading}
          sizeClassName="button_l"
          className="rounded-lg py-3"
          onClick={onContinue}
        />
      </footer>
    </div>
  </div>
);
